import type { MindmapKnoten } from '@beholder/shared/module/mindmap';
import type { Baum } from './baum';

/** Lage eines Knotens in Weltkoordinaten (x/y = linke obere Ecke). */
export interface Lage {
  id: string;
  x: number;
  y: number;
  b: number;
  h: number;
  /** 1 = rechts der Wurzel, -1 = links, 0 = Wurzel. */
  seite: -1 | 0 | 1;
  tiefe: number;
  zeilen: string[];
  hatKinder: boolean;
  eingeklappt: boolean;
  /** Zahl versteckter Nachkommen, wenn eingeklappt. */
  versteckt: number;
}

export interface Anordnung {
  lagen: Map<string, Lage>;
  /** Sichtbare Knoten in Zeichenreihenfolge. */
  liste: Lage[];
  grenzen: { x1: number; y1: number; x2: number; y2: number };
}

export const MASS = {
  schrift: 14,
  schriftWurzel: 17,
  zeile: 19,
  zeileWurzel: 23,
  innenX: 12,
  innenY: 8,
  maxText: 220,
  maxTextWurzel: 260,
  symbol: 20,
  zeichen: 30, // Platz für Anlagen-/Notizzeichen
  abstandX: 56,
  abstandY: 12,
};

let leinwand: CanvasRenderingContext2D | null = null;
let schriftart = '';
const breiten = new Map<string, number>();

function messen(text: string, wurzel: boolean): number {
  if (!leinwand) {
    leinwand = document.createElement('canvas').getContext('2d');
    schriftart = getComputedStyle(document.body).fontFamily || 'system-ui, sans-serif';
  }
  const font = `${wurzel ? 600 : 500} ${wurzel ? MASS.schriftWurzel : MASS.schrift}px ${schriftart}`;
  const s = `${font}|${text}`;
  let b = breiten.get(s);
  if (b === undefined) {
    if (leinwand) {
      leinwand.font = font;
      b = leinwand.measureText(text).width;
    } else b = text.length * (wurzel ? 9.5 : 8);
    if (breiten.size > 20000) breiten.clear();
    breiten.set(s, b);
  }
  return b;
}

export function schrift(): string {
  if (!schriftart) messen('', false);
  return schriftart;
}

/** Bricht einen Titel in Zeilen um (an Leerzeichen, zu lange Wörter hart). */
export function umbrechen(text: string, max: number, wurzel: boolean): string[] {
  const woerter = (text || ' ').split(/\s+/).filter(Boolean);
  const zeilen: string[] = [];
  let akt = '';
  for (let w of woerter) {
    while (messen(w, wurzel) > max && w.length > 1) {
      // Wort hart teilen
      let n = w.length - 1;
      while (n > 1 && messen(w.slice(0, n), wurzel) > max) n--;
      if (akt) {
        zeilen.push(akt);
        akt = '';
      }
      zeilen.push(w.slice(0, n));
      w = w.slice(n);
    }
    const probe = akt ? `${akt} ${w}` : w;
    if (messen(probe, wurzel) <= max) akt = probe;
    else {
      if (akt) zeilen.push(akt);
      akt = w;
    }
  }
  if (akt || !zeilen.length) zeilen.push(akt || ' ');
  return zeilen.slice(0, 6);
}

function groesse(k: MindmapKnoten, wurzel: boolean) {
  const zeilen = umbrechen(k.titel || ' ', wurzel ? MASS.maxTextWurzel : MASS.maxText, wurzel);
  const textB = Math.max(...zeilen.map((z) => messen(z, wurzel)));
  const zeichen = (k.anlagen ? MASS.zeichen : 0) + (k.notizen || k.beschreibung ? MASS.zeichen - 6 : 0);
  const b = Math.ceil(textB + MASS.innenX * 2 + (k.symbol ? MASS.symbol + 4 : 0) + zeichen + (wurzel ? 12 : 0));
  const h = Math.ceil(zeilen.length * (wurzel ? MASS.zeileWurzel : MASS.zeile) + MASS.innenY * 2 + (wurzel ? 10 : 0));
  return { zeilen, b: Math.max(b, wurzel ? 120 : 48), h };
}

/**
 * Automatische Anordnung: Wurzel in der Mitte, Äste der ersten Ebene auf rechts und links verteilt
 * (nach Höhe ausgeglichen), darunter ein waagrechter Baum je Seite.
 */
export function anordnen(baum: Baum, zu: Set<string>): Anordnung {
  const lagen = new Map<string, Lage>();
  const liste: Lage[] = [];
  const w = baum.wurzel;
  if (!w) return { lagen, liste, grenzen: { x1: 0, y1: 0, x2: 0, y2: 0 } };

  const masse = new Map<string, ReturnType<typeof groesse>>();
  const hoehe = new Map<string, number>();
  const zaehle = (id: string): number => {
    let n = 0;
    for (const c of baum.kinder.get(id) ?? []) n += 1 + zaehle(c.id);
    return n;
  };
  const astHoehe = (k: MindmapKnoten, wurzel: boolean): number => {
    const m = groesse(k, wurzel);
    masse.set(k.id, m);
    const kinder = zu.has(k.id) ? [] : (baum.kinder.get(k.id) ?? []);
    let summe = 0;
    kinder.forEach((c, i) => {
      summe += astHoehe(c, false) + (i ? MASS.abstandY : 0);
    });
    const h = Math.max(m.h, summe);
    hoehe.set(k.id, h);
    return h;
  };

  astHoehe(w, true);
  const wm = masse.get(w.id)!;
  const eintragen = (k: MindmapKnoten, x: number, y: number, seite: -1 | 0 | 1, tiefe: number) => {
    const m = masse.get(k.id)!;
    const alleKinder = baum.kinder.get(k.id) ?? [];
    const l: Lage = {
      id: k.id,
      x,
      y,
      b: m.b,
      h: m.h,
      seite,
      tiefe,
      zeilen: m.zeilen,
      hatKinder: alleKinder.length > 0,
      eingeklappt: zu.has(k.id) && alleKinder.length > 0,
      versteckt: zu.has(k.id) ? zaehle(k.id) : 0,
    };
    lagen.set(k.id, l);
    liste.push(l);
    return l;
  };

  const wurzel = eintragen(w, -wm.b / 2, -wm.h / 2, 0, 0);
  const ersteEbene = zu.has(w.id) ? [] : (baum.kinder.get(w.id) ?? []);
  // Ausgleich: rechts füllen, bis die Hälfte der Gesamthöhe erreicht ist
  const gesamt = ersteEbene.reduce((s, c) => s + hoehe.get(c.id)! + MASS.abstandY, 0);
  let rechtsH = 0;
  let teil = ersteEbene.length;
  for (let i = 0; i < ersteEbene.length; i++) {
    if (i > 0 && rechtsH + hoehe.get(ersteEbene[i]!.id)! / 2 > gesamt / 2) {
      teil = i;
      break;
    }
    rechtsH += hoehe.get(ersteEbene[i]!.id)! + MASS.abstandY;
  }
  if (ersteEbene.length === 2) teil = 1;
  const rechts = ersteEbene.slice(0, teil);
  const links = ersteEbene.slice(teil);

  const seiteSetzen = (kinder: MindmapKnoten[], eltern: Lage, seite: -1 | 1, tiefe: number) => {
    const summe = kinder.reduce((s, c, i) => s + hoehe.get(c.id)! + (i ? MASS.abstandY : 0), 0);
    let y = eltern.y + eltern.h / 2 - summe / 2;
    for (const c of kinder) {
      const ah = hoehe.get(c.id)!;
      const m = masse.get(c.id)!;
      const x = seite === 1 ? eltern.x + eltern.b + MASS.abstandX : eltern.x - MASS.abstandX - m.b;
      const l = eintragen(c, x, y + ah / 2 - m.h / 2, seite, tiefe);
      if (!zu.has(c.id)) seiteSetzen(baum.kinder.get(c.id) ?? [], l, seite, tiefe + 1);
      y += ah + MASS.abstandY;
    }
  };
  seiteSetzen(rechts, wurzel, 1, 1);
  seiteSetzen(links, wurzel, -1, 1);

  let x1 = Infinity,
    y1 = Infinity,
    x2 = -Infinity,
    y2 = -Infinity;
  for (const l of liste) {
    x1 = Math.min(x1, l.x - 16);
    y1 = Math.min(y1, l.y - 16);
    x2 = Math.max(x2, l.x + l.b + 16);
    y2 = Math.max(y2, l.y + l.h + 16);
  }
  return { lagen, liste, grenzen: { x1, y1, x2, y2 } };
}

/** Geschwungene Verbindung vom Eltern- zum Kindknoten. */
export function kante(e: Lage, k: Lage): string {
  const rechts = k.seite === 1;
  const sx = e.seite === 0 ? e.x + e.b / 2 + (rechts ? e.b / 2 - 10 : -e.b / 2 + 10) : rechts ? e.x + e.b : e.x;
  const sy = e.y + e.h / 2;
  const zx = rechts ? k.x : k.x + k.b;
  const zy = k.y + k.h / 2;
  const mx = (sx + zx) / 2;
  return `M${sx.toFixed(1)},${sy.toFixed(1)} C${mx.toFixed(1)},${sy.toFixed(1)} ${mx.toFixed(1)},${zy.toFixed(1)} ${zx.toFixed(1)},${zy.toFixed(1)}`;
}
